'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import TripDetailsForm from '@/app/taxi-booking/components/TripDetailsForm';
import VehicleSelector from '@/app/taxi-booking/components/VehicleSelector';
import FareBreakup from '@/app/taxi-booking/components/FareBreakup';
import CouponInput from '@/app/taxi-booking/components/CouponInput';
import { useAuth } from '@/contexts/AuthContext';
import { Car, Shield, FileText, Loader2, MessageCircle, CreditCard, Percent, CheckCircle2, Lock, AlertCircle } from 'lucide-react';

export interface TripFormData {
  name: string;
  mobile: string;
  email: string;
  pickup: string;
  drop: string;
  date: string;
  time: string;
  tripType: 'one-way' | 'round-trip';
  passengers: number;
  luggage: number;
  specialRequests: string;
}

export interface VehicleOption {
  id: string;
  name: string;
  model: string;
  capacity: number;
  baseFare: number;
  perKm: number;
  minKm: number;
  image: string;
  alt: string;
  ac: boolean;
  badge?: string;
}

const VEHICLES: VehicleOption[] = [
{
  id: 'veh-hatchback',
  name: 'Hatchback',
  model: 'Swift / WagonR / Alto',
  capacity: 4,
  baseFare: 1200,
  perKm: 12,
  minKm: 80,
  image: "https://images.unsplash.com/photo-1733759715701-00b393e0d613",
  alt: 'White hatchback car on road',
  ac: true
},
{
  id: 'veh-sedan',
  name: 'Sedan',
  model: 'Dzire / Amaze / Tigor',
  capacity: 4,
  baseFare: 1500,
  perKm: 14,
  minKm: 80,
  image: "https://images.unsplash.com/photo-1724921154428-f21ce7e13b31",
  alt: 'Silver sedan car parked on highway',
  ac: true,
  badge: 'Popular'
},
{
  id: 'veh-suv',
  name: 'SUV',
  model: 'Scorpio / Bolero / Thar',
  capacity: 6,
  baseFare: 2000,
  perKm: 18,
  minKm: 100,
  image: "https://images.unsplash.com/photo-1707313344368-3fd73b00f579",
  alt: 'Black SUV on mountain road',
  ac: true,
  badge: 'Hill Expert'
},
{
  id: 'veh-innova',
  name: 'Innova',
  model: 'Toyota Innova / Crysta',
  capacity: 7,
  baseFare: 2400,
  perKm: 20,
  minKm: 100,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c5bf1600-1780426302740.png",
  alt: 'Toyota Innova van on mountain highway',
  ac: true,
  badge: 'Best Seller'
},
{
  id: 'veh-ertiga',
  name: 'Ertiga / Rumion',
  model: 'Suzuki Ertiga / Toyota Rumion',
  capacity: 6,
  baseFare: 1800,
  perKm: 16,
  minKm: 80,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_130a90573-1768531681430.png",
  alt: 'Maroon MPV Ertiga parked near hills',
  ac: true
},
{
  id: 'veh-crysta',
  name: 'Innova Crysta',
  model: 'Toyota Innova Crysta',
  capacity: 7,
  baseFare: 2800,
  perKm: 22,
  minKm: 100,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10b3582ff-1764720422661.png",
  alt: 'Toyota Innova Crysta luxury MPV on road',
  ac: true,
  badge: 'Premium'
},
{
  id: 'veh-tempo-12',
  name: 'Tempo Traveller 12',
  model: 'Force Tempo 12-Seater',
  capacity: 12,
  baseFare: 3500,
  perKm: 26,
  minKm: 150,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11fa0872b-1768440186823.png",
  alt: 'White tempo traveller bus on mountain road',
  ac: true,
  badge: 'Group'
},
{
  id: 'veh-tempo-17',
  name: 'Tempo Traveller 17',
  model: 'Force Tempo 17-Seater',
  capacity: 17,
  baseFare: 4200,
  perKm: 30,
  minKm: 150,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_114e4d671-1768440186864.png",
  alt: 'Large white tempo traveller bus for group travel',
  ac: true,
  badge: 'Large Group'
},
{
  id: 'veh-urbania',
  name: 'Urbania',
  model: 'Force Urbania Luxury Van',
  capacity: 17,
  baseFare: 5500,
  perKm: 35,
  minKm: 150,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1be0024a8-1767645973805.png",
  alt: 'Modern luxury Force Urbania van for premium group travel',
  ac: true,
  badge: 'Luxury'
}];


export function computeFare(vehicle: VehicleOption | null, tripType: string, estimatedKm: number) {
  if (!vehicle) return null;
  const km = Math.max(estimatedKm, vehicle.minKm);
  const baseFare = vehicle.baseFare;
  const kmCharge = km * vehicle.perKm;
  const driverAllowance = 300;
  const nightCharges = tripType === 'round-trip' ? 250 : 0;
  const hillCharges = Math.round(km * 1.5);
  const stateTax = Math.round((baseFare + kmCharge) * 0.02);
  const permit = 500;
  const tollTax = 350;
  const parking = 100;
  const subtotal = baseFare + kmCharge + driverAllowance + nightCharges + hillCharges + stateTax + permit + tollTax + parking;
  const gst = Math.round(subtotal * 0.05);
  const returnCharges = tripType === 'round-trip' ? Math.round(subtotal * 0.85) : 0;
  const total = subtotal + gst + returnCharges;
  return {
    baseFare,
    kmCharge,
    km,
    driverAllowance,
    nightCharges,
    hillCharges,
    stateTax,
    permit,
    tollTax,
    parking,
    subtotal,
    gst,
    returnCharges,
    total
  };
}

function generateBookingId(): string {
  const prefix = 'GJT';
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
}

// Load Razorpay script dynamically and resolve when ready
function loadRazorpayScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).Razorpay) {
      resolve();
      return;
    }
    const existing = document.getElementById('razorpay-checkout-js');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Razorpay script')));
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout script. Please check your internet connection.'));
    document.head.appendChild(script);
  });
}

type PaymentOption = '20percent' | '100percent';
type PaymentStatus = 'idle' | 'creating-order' | 'opening-checkout' | 'verifying' | 'confirmed' | 'failed';

const PAYMENT_STATUS_MESSAGES: Record<PaymentStatus, { icon: string; text: string; color: string } | null> = {
  idle: null,
  'creating-order': { icon: '🔒', text: 'Initializing secure payment...', color: 'text-blue-700' },
  'opening-checkout': { icon: '🔄', text: 'Opening Razorpay checkout...', color: 'text-blue-700' },
  verifying: { icon: '🔐', text: 'Verifying payment signature...', color: 'text-amber-700' },
  confirmed: { icon: '✅', text: 'Payment confirmed!', color: 'text-green-700' },
  failed: { icon: '❌', text: 'Payment failed. Please try again.', color: 'text-red-700' },
};

export default function TaxiBookingClient() {
  const router = useRouter();
  const { user } = useAuth();
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleOption | null>(VEHICLES[1]);
  const [tripType, setTripType] = useState<'one-way' | 'round-trip'>('one-way');
  const [estimatedKm] = useState(365);
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [appliedCouponCode, setAppliedCouponCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentOption, setPaymentOption] = useState<PaymentOption>('20percent');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle');
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<TripFormData>({
    defaultValues: {
      name: '',
      mobile: '',
      email: '',
      pickup: 'Delhi',
      drop: 'Shimla',
      date: '',
      time: '',
      tripType: 'one-way',
      passengers: 2,
      luggage: 1,
      specialRequests: ''
    }
  });

  const fareData = computeFare(selectedVehicle, tripType, estimatedKm);
  const finalTotal = fareData ? Math.max(0, fareData.total - couponDiscount) : 0;
  const advanceAmount = Math.ceil(finalTotal * 0.2);
  const payableNow = paymentOption === '20percent' ? advanceAmount : finalTotal;
  const pendingAmount = paymentOption === '20percent' ? finalTotal - advanceAmount : 0;

  const onSubmit = async (data: TripFormData) => {
    if (!selectedVehicle) {
      toast.error('Please select a vehicle');
      return;
    }

    setIsSubmitting(true);
    setPaymentStatus('creating-order');
    setPaymentError(null);

    try {
      // Step 1: Load Razorpay script
      await loadRazorpayScript();

      const bookingId = generateBookingId();

      // Step 2: Create Razorpay order via server
      const orderRes = await fetch('/api/razorpay/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: payableNow,
          currency: 'INR',
          bookingId,
        }),
      });

      const orderData = await orderRes.json();

      if (!orderData.success || !orderData.orderId) {
        throw new Error(orderData.error || 'Failed to create payment order');
      }

      setPaymentStatus('opening-checkout');

      // Step 3: Open Razorpay checkout
      await new Promise<void>((resolve, reject) => {
        const options = {
          key: orderData.keyId,
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'Ghumo Ji Travels',
          description: `Taxi Booking – ${data.pickup} to ${data.drop}`,
          image: '/assets/images/Screenshot_20260806-205147_WhatsApp_Business-1786029755512.png',
          order_id: orderData.orderId,
          prefill: {
            name: data.name,
            email: data.email,
            contact: `91${data.mobile}`,
          },
          notes: {
            bookingId,
            pickup: data.pickup,
            drop: data.drop,
            vehicle: selectedVehicle?.name,
            paymentType: paymentOption === '20percent' ? '20% Advance' : '100% Full Payment',
          },
          theme: { color: '#0F4C81' },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handler: async (response: { razorpay_payment_id: string; razorpay_order_id: string; razorpay_signature: string }) => {
            setPaymentStatus('verifying');

            try {
              // Step 4: Verify payment signature server-side
              const verifyRes = await fetch('/api/razorpay/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              });

              const verifyData = await verifyRes.json();

              if (!verifyData.success) {
                throw new Error(verifyData.error || 'Payment verification failed');
              }

              setPaymentStatus('confirmed');

              // Step 5: Save booking to Supabase
              const saveRes = await fetch('/api/booking/save', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  booking_id: bookingId,
                  user_id: user ? (user as { id: string }).id : null,
                  customer_name: data.name,
                  customer_mobile: data.mobile,
                  customer_email: data.email,
                  pickup: data.pickup,
                  drop_location: data.drop,
                  travel_date: data.date,
                  travel_time: data.time,
                  vehicle: selectedVehicle?.name,
                  vehicle_model: selectedVehicle?.model,
                  passengers: data.passengers,
                  trip_type: tripType,
                  total_amount: finalTotal,
                  amount_paid: payableNow,
                  pending_amount: pendingAmount,
                  coupon_code: appliedCouponCode || null,
                  coupon_discount: couponDiscount,
                  booking_status: 'confirmed',
                  payment_status: paymentOption === '100percent' ? 'fully_paid' : 'advance_paid',
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  special_requests: data.specialRequests,
                }),
              });
              const saveData = await saveRes.json();
              if (!saveData.success && !saveData.duplicate) {
                console.error('Booking save failed:', saveData.error);
              }

              toast.success('Payment verified! Booking confirmed.');
              resolve();
              router.push(`/booking-confirmation?id=${bookingId}`);
            } catch (verifyError) {
              reject(verifyError);
            }
          },
          modal: {
            ondismiss: () => {
              setIsSubmitting(false);
              setPaymentStatus('idle');
              toast.info('Payment cancelled. Your booking details are saved.');
              resolve();
            },
          },
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rzp = new (window as any).Razorpay(options);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rzp.on('payment.failed', (res: any) => {
          setIsSubmitting(false);
          setPaymentStatus('failed');
          const errMsg = res.error?.description || 'Payment failed';
          toast.error(errMsg);
          reject(new Error(errMsg));
          router.push(`/payment-failure?error=${encodeURIComponent(errMsg)}`);
        });
        rzp.open();
      });

    } catch (error) {
      setIsSubmitting(false);
      setPaymentStatus('failed');
      const errMsg = error instanceof Error ? error.message : 'Payment setup failed';
      setPaymentError(errMsg);
      toast.error(errMsg);
    }
  };

  const statusInfo = PAYMENT_STATUS_MESSAGES[paymentStatus];

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Payment Error Banner */}
        {paymentError && (
          <div className="mb-6 bg-red-50 border border-red-300 rounded-2xl p-5 flex items-start gap-4">
            <AlertCircle size={22} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-bold text-red-800 mb-1">Payment Error</p>
              <p className="text-sm text-red-700 mb-3">{paymentError}</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentError(null)}
                  className="text-sm font-semibold text-red-700 underline"
                >
                  Try Again
                </button>
                <a
                  href="https://wa.me/918894915859?text=Hi%20Ghumo%20Ji%20Travels%2C%20I%20need%20help%20with%20payment."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[#25D366] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-green-500 transition-colors"
                >
                  <MessageCircle size={15} />
                  Book via WhatsApp
                </a>
                <a
                  href="tel:+918894915859"
                  className="flex items-center gap-2 bg-white border border-red-300 text-red-800 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Call Us
                </a>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left: Form + Vehicle Selector */}
          <div className="xl:col-span-2 flex flex-col gap-6">
            {/* Customer Details */}
            <div className="bg-white rounded-2xl border border-border shadow-sm p-6">
              <h2 className="font-bold text-foreground text-lg mb-5 flex items-center gap-2">
                <Shield size={18} className="text-primary" />
                Your Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`input-field ${errors.name ? 'border-red-400' : ''}`}
                    placeholder="Your full name"
                    {...register('name', { required: 'Name is required' })}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    className={`input-field ${errors.mobile ? 'border-red-400' : ''}`}
                    placeholder="+91 XXXXX XXXXX"
                    {...register('mobile', {
                      required: 'Mobile number is required',
                      pattern: { value: /^[6-9]\d{9}$/, message: 'Enter valid 10-digit mobile' }
                    })}
                  />
                  {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    className={`input-field ${errors.email ? 'border-red-400' : ''}`}
                    placeholder="your@email.com"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter valid email' }
                    })}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                </div>
              </div>
            </div>

            <TripDetailsForm
              register={register}
              errors={errors}
              tripType={tripType}
              setTripType={setTripType} />
            
            <VehicleSelector
              vehicles={VEHICLES}
              selectedVehicle={selectedVehicle}
              onSelect={setSelectedVehicle} />
            
            <CouponInput
              onApply={(discount, code) => {
                setCouponApplied(discount > 0);
                setCouponDiscount(discount);
                setAppliedCouponCode(code);
                if (discount > 0) toast.success(`Coupon applied! You saved ₹${discount.toLocaleString('en-IN')}`);
              }}
              applied={couponApplied}
              bookingAmount={fareData?.total || 0} />

            {/* Payment Option Selection */}
            {fareData && (
              <div className="bg-white rounded-2xl border border-border shadow-sm p-6">
                <h2 className="font-bold text-foreground text-lg mb-5 flex items-center gap-2">
                  <CreditCard size={18} className="text-primary" />
                  Payment Option
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setPaymentOption('20percent')}
                    className={`relative p-5 rounded-xl border-2 text-left transition-all duration-150 ${
                      paymentOption === '20percent' ? 'border-primary bg-primary-light' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {paymentOption === '20percent' && (
                      <CheckCircle2 size={18} className="absolute top-3 right-3 text-primary" />
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <Percent size={16} className="text-secondary" />
                      <span className="font-bold text-foreground text-sm">Pay 20% Advance</span>
                      <span className="bg-secondary text-white text-xs px-2 py-0.5 rounded-full">Recommended</span>
                    </div>
                    <p className="text-2xl font-extrabold text-primary font-tabular mb-1">
                      ₹{advanceAmount.toLocaleString('en-IN')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Pay now to confirm. Remaining ₹{(finalTotal - advanceAmount).toLocaleString('en-IN')} before/during trip.
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPaymentOption('100percent')}
                    className={`relative p-5 rounded-xl border-2 text-left transition-all duration-150 ${
                      paymentOption === '100percent' ? 'border-primary bg-primary-light' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {paymentOption === '100percent' && (
                      <CheckCircle2 size={18} className="absolute top-3 right-3 text-primary" />
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCard size={16} className="text-accent" />
                      <span className="font-bold text-foreground text-sm">Pay 100% Online</span>
                    </div>
                    <p className="text-2xl font-extrabold text-primary font-tabular mb-1">
                      ₹{finalTotal.toLocaleString('en-IN')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Pay full amount now. Enjoy a completely hassle-free travel experience.
                    </p>
                  </button>
                </div>

                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-xs text-amber-800">
                    {paymentOption === '20percent' ?'⚡ Minimum 20% advance payment is required to confirm your booking. The remaining balance can be paid before or during the trip.' :'✅ Pay the full booking amount online and enjoy a hassle-free travel experience.'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right: Fare Breakup + Summary */}
          <div className="flex flex-col gap-5 xl:sticky xl:top-24 xl:self-start">
            {fareData && selectedVehicle ? (
              <>
                <FareBreakup
                  fare={fareData}
                  vehicle={selectedVehicle}
                  tripType={tripType}
                  estimatedKm={estimatedKm}
                  couponDiscount={couponDiscount}
                  finalTotal={finalTotal} />

                {/* Booking Summary Card */}
                <div className="bg-white rounded-2xl border border-border shadow-sm p-5">
                  <h3 className="font-bold text-foreground text-base mb-4">Booking Summary</h3>
                  <div className="space-y-3 mb-5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Vehicle</span>
                      <span className="font-semibold text-foreground">{selectedVehicle.name}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Trip Type</span>
                      <span className="font-semibold text-foreground capitalize">
                        {tripType === 'round-trip' ? 'Round Trip' : 'One Way'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Total Fare</span>
                      <span className="font-extrabold text-primary font-tabular">₹{finalTotal.toLocaleString('en-IN')}</span>
                    </div>
                    {paymentOption === '20percent' && (
                      <>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Pay Now (20%)</span>
                          <span className="font-bold text-secondary font-tabular">₹{advanceAmount.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Pay Later</span>
                          <span className="font-semibold text-foreground font-tabular">₹{pendingAmount.toLocaleString('en-IN')}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="border-t border-border pt-4 mb-5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-foreground text-base">Pay Now</span>
                      <span className="font-extrabold text-primary text-2xl font-tabular">
                        ₹{payableNow.toLocaleString('en-IN')}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Inclusive of GST · No hidden charges</p>
                  </div>

                  <div className="mb-5">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Pay via</p>
                    <div className="flex flex-wrap gap-2">
                      {['Razorpay', 'UPI/QR', 'PhonePe', 'Credit Card', 'Net Banking'].map((method) => (
                        <span key={`pay-method-${method}`} className="text-xs bg-muted text-muted-foreground px-2.5 py-1 rounded-full border border-border">
                          {method}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Payment Status Messages */}
                  {statusInfo && (
                    <div className={`mb-4 flex items-center gap-2 p-3 rounded-xl bg-muted/50 border border-border ${statusInfo.color}`}>
                      <span className="text-base">{statusInfo.icon}</span>
                      <span className="text-sm font-medium">{statusInfo.text}</span>
                      {(paymentStatus === 'creating-order' || paymentStatus === 'opening-checkout' || paymentStatus === 'verifying') && (
                        <Loader2 size={14} className="animate-spin ml-auto flex-shrink-0" />
                      )}
                    </div>
                  )}

                  <div className="flex flex-col gap-2 mb-5">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Shield size={13} className="text-secondary flex-shrink-0" />
                      Verified driver assigned within 30 minutes
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <FileText size={13} className="text-primary flex-shrink-0" />
                      GST invoice sent to your email
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <MessageCircle size={13} className="text-[#25D366] flex-shrink-0" />
                      Booking confirmation on WhatsApp
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Lock size={13} className="text-primary flex-shrink-0" />
                      256-bit SSL secured payment
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-accent w-full justify-center text-base py-3.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        {paymentStatus === 'verifying' ? 'Verifying Payment...' : 'Processing...'}
                      </>
                    ) : (
                      <>
                        <CreditCard size={18} />
                        Pay ₹{payableNow.toLocaleString('en-IN')} &amp; Confirm
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-muted-foreground mt-3">
                    By booking, you agree to our{' '}
                    <a href="/" className="text-primary hover:underline">Terms &amp; Conditions</a>
                    {' '}and{' '}
                    <a href="/" className="text-primary hover:underline">Cancellation Policy</a>
                  </p>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-2xl border border-border p-8 text-center">
                <Car className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground font-medium">Select a vehicle to see fare</p>
              </div>
            )}
          </div>
        </div>
      </form>
    </>
  );
}